import { useEffect, useRef } from "react";
import { Badge, Box, Flex, Text, VStack, useColorModeValue } from "@chakra-ui/react";
import type { ReactNode } from "react";
import CandidateOnboardingCard from "../CandidateOnboardingCard";
import { OnflyLoading } from "../../../../tools/OnflyLoading";
import type { Candidate } from "../../../../types/onboarding";
import type { WhitelistEntry } from "../../../../types/whitelist";

export interface KanbanColumnData {
  key: string;
  label: string;
  dot: string;
  candidates: Candidate[];
  total: number;
  hasMore: boolean;
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  hasFetched: boolean;
  onLoadMore: () => void;
  /** Elemento extra renderizado no header da coluna (ex: chip de filtro). */
  headerExtra?: ReactNode;
}

export interface KanbanColumnProps extends Omit<KanbanColumnData, "key"> {
  selectedCandidateIds: Set<string | number>;
  onToggleSelect?: (candidateId: string | number) => void;
  onViewDetail: (candidate: Candidate) => void;
  onCancel: (candidate: Candidate) => void;
  onReactivate: (candidate: Candidate) => void;
  showHrProgress?: boolean;
  whitelistMap?: Record<string, WhitelistEntry>;
}

/** Coluna de Kanban: cabeçalho (status + contagem) + corpo com scroll
 * infinito próprio — busca a próxima página só quando o fim DESTA coluna
 * entra em vista, sem afetar as demais colunas. */
export const KanbanColumn = ({
  label, dot, candidates, total, hasMore, isLoadingInitial, isLoadingMore, hasFetched, onLoadMore,
  headerExtra,
  selectedCandidateIds, onToggleSelect, onViewDetail, onCancel, onReactivate, showHrProgress, whitelistMap,
}: KanbanColumnProps) => {
  const columnBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const headerColor = useColorModeValue("gray.700", "gray.200");
  const mutedColor = useColorModeValue("gray.400", "gray.500");

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Observa o "fim" da própria coluna (root = scroll container da coluna,
  // não a janela) — cada coluna dispara sua paginação de forma isolada.
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { root, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  return (
    <Flex
      direction="column"
      flex="1 1 0"
      minW={0}
      maxW="380px"
      bg={columnBg}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      h="100%"
    >
      <Flex direction="column" gap={1} px={3} py={3} borderBottomWidth="1px" borderColor={borderColor} flexShrink={0}>
        <Flex align="center" gap={2}>
          <Box w={2} h={2} borderRadius="full" bg={dot} flexShrink={0} />
          <Text fontSize="xs" fontWeight="bold" color={headerColor} letterSpacing="wider" textTransform="uppercase" flex={1} noOfLines={1}>
            {label}
          </Text>
          <Badge borderRadius="full" colorScheme="gray" fontSize="xs" flexShrink={0}>
            {total}
          </Badge>
        </Flex>
        {headerExtra && <Box pl={4}>{headerExtra}</Box>}
      </Flex>

      <Box ref={scrollRef} flex={1} overflowY="auto" px={2} py={2}>
        {isLoadingInitial ? (
          <Flex justify="center" py={6}><OnflyLoading containerSize={40} /></Flex>
        ) : !hasFetched ? null : candidates.length === 0 ? (
          <Text color={mutedColor} textAlign="center" py={4} fontSize="sm">
            Nenhum candidato.
          </Text>
        ) : (
          <VStack spacing={2} align="stretch">
            {candidates.map((c) => (
              <CandidateOnboardingCard
                key={c.user_id}
                candidate={c}
                onViewDetail={onViewDetail}
                onCancel={onCancel}
                onReactivate={onReactivate}
                isSelected={selectedCandidateIds.has(c.user_id)}
                onToggleSelect={onToggleSelect}
                showHrProgress={showHrProgress}
                whitelistEntry={whitelistMap?.[c.email]}
              />
            ))}
            {hasMore && <Box ref={sentinelRef} h="1px" flexShrink={0} />}
            {isLoadingMore && (
              <Flex justify="center" py={2}><OnflyLoading containerSize={24} /></Flex>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );
};
